import { Link } from 'react-router-dom';

import { useCategoryStats, useGapBuckets, usePositionBuckets, useProducts, useSummary } from '../api/queries';
import type { ProductComparison } from '../api/types';
import { CategoryGapChart } from '../charts/CategoryGapChart';
import { GapHistogram } from '../charts/GapHistogram';
import { PositionComposition } from '../charts/PositionComposition';
import { DIVERGE, SERIES, STATUS } from '../charts/palette';
import { Card } from '../components/Card';
import { Delta } from '../components/Delta';
import { PositionBadge } from '../components/PositionBadge';
import { ShopName } from '../components/ShopName';
import { Failed, Loading } from '../components/States';
import { StatTile } from '../components/StatTile';
import { count, money, percent, relative } from '../utils/format';

export function DashboardPage() {
  const summary = useSummary();
  const positions = usePositionBuckets();
  const gaps = useGapBuckets();
  const categories = useCategoryStats(12);
  const worst = useProducts({ position: 'expensive', sort: 'gapPct', direction: 'desc', perPage: 8, page: 1 });

  if (summary.isError) {
    return <Failed error={summary.error} />;
  }

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Price position overview</h1>
          <p className="page__lede">
            Where your catalogue sits against every other Heureka.sk shop selling the same product, and how much
            money the gaps add up to.
          </p>
        </div>
        <div className="muted">
          Feed synced {relative(summary.data?.lastFeedSyncAt)} · last scan {relative(summary.data?.lastScanAt)}
        </div>
      </header>

      {summary.isLoading ? (
        <Loading label="Loading summary…" />
      ) : (
        summary.data && (
          <>
            <div className="grid grid--tiles">
              <StatTile
                label="You are cheapest"
                value={count(summary.data.cheapestCount)}
                foot={`beating a competitor, of ${count(summary.data.scannedProducts)} scanned`}
                accent={DIVERGE.low}
              />
              <StatTile
                label="Only seller"
                value={count(summary.data.soleSellerCount)}
                foot="listed on Heureka with no competing offer"
                accent={SERIES[6]}
              />
              <StatTile
                label="Somebody undercuts you"
                value={count(summary.data.expensiveCount + summary.data.competitiveCount)}
                foot={`${count(summary.data.expensiveCount)} outside the top 3`}
                accent={DIVERGE.high}
              />
              <StatTile
                label="Mean gap to market low"
                value={<Delta value={summary.data.avgGapPct} />}
                foot="positive means you are above the cheapest offer"
                accent={SERIES[3]}
              />
              <StatTile
                label="Sitting above market"
                value={money(summary.data.totalOverpriceEur)}
                foot="summed euros above the cheapest offer"
                accent={STATUS.critical}
              />
              <StatTile
                label="Unused margin"
                value={money(summary.data.totalHeadroomEur)}
                foot="euros you could add and still be cheapest"
                accent={STATUS.good}
              />
              <StatTile
                label="Matched on Heureka"
                value={count(summary.data.matchedProducts)}
                foot={`${count(summary.data.notFoundProducts)} not listed · ${count(
                  summary.data.neverScannedProducts,
                )} not scanned yet`}
                accent={SERIES[2]}
              />
            </div>

            {summary.data.neverScannedProducts > 0 && (
              <div className="banner">
                <span aria-hidden="true">◷</span>
                <div>
                  <strong>{count(summary.data.neverScannedProducts)} products have not been scanned yet.</strong>
                  <div className="muted" style={{ marginTop: 4 }}>
                    The crawler works through the catalogue slowly on purpose, so the numbers above fill in over
                    days rather than minutes. <Link to="/crawler">Check crawler progress →</Link>
                  </div>
                </div>
              </div>
            )}
          </>
        )
      )}

      <div className="grid grid--halves">
        <Card
          title="Where your prices sit"
          note="Share of the active catalogue by position in the Heureka offer list"
        >
          {positions.isLoading ? (
            <Loading />
          ) : positions.isError ? (
            <Failed error={positions.error} />
          ) : (
            <PositionComposition buckets={positions.data ?? []} />
          )}
        </Card>

        <Card title="Distance from the cheapest offer" note="Products per gap band">
          {gaps.isLoading ? (
            <Loading />
          ) : gaps.isError ? (
            <Failed error={gaps.error} />
          ) : (
            <GapHistogram buckets={gaps.data ?? []} />
          )}
        </Card>
      </div>

      <Card title="Category competitiveness" note="Mean gap to the market low, by Heureka category. Hover for the full category path.">
        {categories.isLoading ? (
          <Loading />
        ) : categories.isError ? (
          <Failed error={categories.error} />
        ) : (
          <CategoryGapChart stats={categories.data ?? []} />
        )}
      </Card>

      <Card
        title="Biggest gaps right now"
        note="Products where you are furthest above the cheapest offer"
        action={<Link to="/products?position=expensive" className="button">See all</Link>}
      >
        {worst.isLoading ? (
          <Loading />
        ) : worst.isError ? (
          <Failed error={worst.error} />
        ) : (worst.data?.items.length ?? 0) === 0 ? (
          <div className="state">Nothing above the market low. Nice.</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="num">Your price</th>
                  <th className="num">Market low</th>
                  <th className="num">Gap</th>
                  <th>Cheapest shop</th>
                  <th>Position</th>
                </tr>
              </thead>
              <tbody>
                {worst.data?.items.map((product: ProductComparison) => (
                  <tr key={product.id}>
                    <td>
                      <Link to={`/products/${product.id}`} className="cell-product">
                        {product.imageUrl && (
                          <img className="cell-product__img" src={product.imageUrl} alt="" loading="lazy" />
                        )}
                        <span>
                          <span className="cell-product__name">{product.name}</span>
                          <div className="cell-product__meta">{product.ean ?? 'no EAN'}</div>
                        </span>
                      </Link>
                    </td>
                    <td className="num">{money(product.ourPrice)}</td>
                    <td className="num">{money(product.minPrice)}</td>
                    <td className="num">
                      <Delta value={product.priceGapPct} />
                      <div className="cell-product__meta">{money(product.priceGapAbs)}</div>
                    </td>
                    <td className="secondary">
                      <ShopName product={product} />
                    </td>
                    <td>
                      <PositionBadge position={product.position} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="muted" style={{ fontSize: 12 }}>
        Mean gap across scanned products: {percent(summary.data?.avgGapPct)} · every figure comes from the newest
        successful scan of each product.
      </div>
    </div>
  );
}
